/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { FlexRow } from '../Flex';
import { ButtonProps } from '../Button';
import IconButton from "../IconButton";
import { useSearchParams } from 'react-router';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import Icon from "../Icon";
import { usePagination } from '@/shared/hooks/use-pagination';

export type PaginationProps = {
  numPages: number;
};
export const Pagination: Component<PaginationProps> = ({ numPages, sx }) => {
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get("page") ?? "1");

  return (
    <FlexRow
      sx={{
        alignItems: "center",
        width: "min-content",
        gap: 2,
        ...sx,
      }}
    >
      <PageButton {...{ targetPage: 1, page, disabled: page <= 1 }}>
        <Icon as={DoubleArrowLeftIcon} />
      </PageButton>

      <PageButton {...{ targetPage: page - 1, page, disabled: page <= 1 }}>
            <Icon as={ChevronLeftIcon} />
      </PageButton>

      <PageButton
        {...{ targetPage: page, page }}
        disabled
        sx={{ cursor: "default" }}
      >
        {page}
      </PageButton>

      <PageButton
        {...{
          targetPage: page + 1,
          page,

          disabled: page >= numPages,
        }}
      >
            <Icon as={ChevronRightIcon} />
      </PageButton>

      <PageButton
        {...{
          targetPage: numPages,
          page,

          disabled: page >= numPages,
        }}
      >
            <Icon as={DoubleArrowRightIcon} />
      </PageButton>
    </FlexRow>
  );
};

type PageButtonProps = {
  targetPage: any;
  page: number;
} & ButtonProps;
const PageButton: ComponentWithChildren<PageButtonProps> = ({
  page,
  targetPage,
  disabled = false,
  children,
  variant = "outlined",
  sx,
  ...props
}) => {
  const [pagination, setPagination] = usePagination();

  return (
    <IconButton
      variant={variant}
      size="small"
      color="text.main"
      sx={{ px: 1, ...sx }}
      disabled={disabled}
      onClick={() => setPagination({ page: String(targetPage) })}
      {...props}
    >
      {children}
    </IconButton>
  );
};
